import { fetcher } from '@data/utils/api/fetcher/fetcher';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { useApplications } from '@data/hooks/api/use-applications-api/use-applications-api';

export interface ClassByEdApp {
  classId: string;
  name: string;
}

export type ClassType = 'CLASS' | 'SECTION' | 'GROUP';

export interface UseGetClassesByEdAppApiParams {
  orgId: string;
  userId: string;
  edApp: string;
  classType?: ClassType;
  /**
   * The product codes to ask for. Defaults to the signed-in user's own
   * entitlements, which is the wrong question for an admin reading another
   * educator's classes: an admin need not hold the product being reported on,
   * and with no codes the endpoint has nothing to filter by.
   */
  productCodes?: string[] | undefined;
}

interface RawClassListItem {
  id: string;
  name: string;
}

/**
 * The Access endpoint already filters to classes holding the product's student
 * entitlement, so the list it returns is the answer — no per-class lookup.
 */
async function getClassesByEdApp({
  orgId,
  userId,
  classType,
  productCodes,
}: {
  orgId: string;
  userId: string;
  classType: ClassType;
  productCodes: string[];
}): Promise<ClassByEdApp[]> {
  const classes = await fetcher<RawClassListItem[]>(
    `/classes/orgs/${orgId}/users/${userId}/class-types/${classType}`,
    {
      service: '/roster',
      version: '/v3',
      queryParameters: { productCodes },
    }
  );

  return classes.map((cls) => ({ classId: cls.id, name: cls.name }));
}

/**
 * Query options for use with `queryClient.ensureQueryData` in route loaders.
 *
 * `productCodes` is explicit here: a loader has no component to read the
 * applications from, and the hook's own default is the signed-in user's
 * entitlements, which a guard has already resolved.
 */
export function classesByEdAppQuery({
  orgId,
  userId,
  edApp,
  classType = 'CLASS',
  productCodes,
}: UseGetClassesByEdAppApiParams & { productCodes: string[] }) {
  return {
    queryKey: [
      'classes-by-ed-app',
      orgId,
      userId,
      classType,
      edApp,
      productCodes,
    ],
    queryFn: () =>
      getClassesByEdApp({ orgId, userId, classType, productCodes }),
  };
}

export function useGetClassesByEdAppApi(
  {
    orgId,
    userId,
    edApp,
    classType = 'CLASS',
    productCodes: givenProductCodes,
  }: UseGetClassesByEdAppApiParams,
  customQueryOptions?: Partial<UseQueryOptions<ClassByEdApp[]>>
) {
  const { data: applications } = useApplications();

  const productCodes =
    givenProductCodes ??
    applications
      ?.find((app) => app.applicationCode === edApp)
      ?.products.map((product) => product.productCode) ??
    [];

  const { enabled = true } = customQueryOptions ?? {};

  return useQuery<ClassByEdApp[]>({
    ...classesByEdAppQuery({ orgId, userId, edApp, classType, productCodes }),
    ...customQueryOptions,
    // Last, so a caller's own `enabled` narrows this guard rather than
    // replacing it: a request with no ids is never worth sending.
    enabled:
      enabled && Boolean(orgId && userId && edApp) && productCodes.length > 0,
  });
}
